import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import PageArrival from '../components/PageArrival';
import { PiggyBank as A1, CreditCard as A2, Receipt as A3, TrendingUp as A4, ShieldCheck as A5, BookOpen as A6 } from 'lucide-react';

import { AppLayout, GlassCard, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import { Wallet, CreditCard, Rocket, PiggyBank, ShieldAlert, RefreshCw, Sparkles, CheckCircle, Circle, Trophy, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import PatriciaPageGuide from '../components/PatriciaPageGuide';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = { wallet: Wallet, credit: CreditCard, rocket: Rocket, piggy: PiggyBank, shield: ShieldAlert };
const ACCENTS = [
  'from-amber-500/20 to-orange-600/10 text-amber-300 ring-amber-400/40',
  'from-emerald-500/20 to-green-600/10 text-emerald-300 ring-emerald-400/40',
  'from-cyan-500/20 to-blue-600/10 text-cyan-300 ring-cyan-400/40',
  'from-purple-500/20 to-fuchsia-600/10 text-purple-300 ring-purple-400/40',
  'from-rose-500/20 to-red-600/10 text-rose-300 ring-rose-400/40',
];

const MILESTONE_GOAL = 3;

export default function FinancialLiteracyPage() {
  const [arrived, setArrived] = useState(false);
  const { getAuthHeader } = useAuth();
  const [lessons, setLessons] = useState([]);
  const [profile, setProfile] = useState({});
  const [completedTips, setCompletedTips] = useState([]);
  const [stale, setStale] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (refresh = false) => {
    try {
      const r = await axios.get(`${API}/financial-literacy${refresh ? '?refresh=1' : ''}`, getAuthHeader());
      setLessons(r.data.lessons || []);
      setProfile(r.data.profile || {});
      setCompletedTips(r.data.completed_tips || []);
      setStale(!!r.data.stale);
    } catch { toast.error('Could not load your financial literacy plan.'); }
    finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { load(false); /* eslint-disable-next-line */ }, []);

  const refresh = () => { setRefreshing(true); toast.info('Refreshing your plan…'); load(true); };

  const toggleTip = async (tip) => {
    const wasDone = completedTips.includes(tip);
    const optimistic = wasDone ? completedTips.filter((t) => t !== tip) : [...completedTips, tip];
    setCompletedTips(optimistic);
    try {
      const r = await axios.post(`${API}/financial-literacy/toggle-tip`, { tip }, getAuthHeader());
      setCompletedTips(r.data.completed_tips || optimistic);
      if (!wasDone && (r.data.completed_tips || []).length === MILESTONE_GOAL) {
        toast.success('🏆 Money Smart badge unlocked, it counts toward your Launch Readiness!');
      }
    } catch {
      setCompletedTips(completedTips); // revert
      toast.error('Could not save that. Try again.');
    }
  };

  const doneCount = completedTips.length;
  const milestoneMet = doneCount >= MILESTONE_GOAL;
  const progressPct = Math.min(100, Math.round((doneCount / MILESTONE_GOAL) * 100));

  if (!arrived) return (
    <AppLayout>
      <PageArrival
        testid="literacy-arrival"
        eyebrow="Financial Literacy"
        title="The money lessons nobody taught you."
        subtitle="Credit, taxes, interest, insurance and the first paycheque, explained with your own numbers, in short lessons you can finish in a sitting. Not theory, and never patronising."
        accent="#34d399"
        second="#fbbf24"
        ctaLabel="Start learning"
        narration={"This is Financial Literacy. Everything here is short, plain and built around your own numbers instead of made-up examples. How credit scores actually move. Why your first paycheque is smaller than you expected. What interest does to a loan over ten years. Which insurance is genuinely necessary. Finish a lesson in a sitting, and you will spot the money mistakes that quietly cost people years."}
        facts={[
          { icon: A6, head: "Short lessons", sub: "Each finishes in one sitting" },
          { icon: A1, head: "Your own numbers", sub: "Built from the life you designed" },
          { icon: A5, head: "No selling", sub: "No products, no affiliates, no pitch" }
        ]}
        modules={[
          { icon: A2, label: "Credit and score", line: "What moves it, what wrecks it, and how fast it recovers", hue: "#f472b6" },
          { icon: A3, label: "Your first paycheque", line: "Gross to net, and where the rest went", hue: "#22d3ee" },
          { icon: A1, label: "Saving and buffers", line: "How much is enough, and where to keep it", hue: "#34d399" },
          { icon: A4, label: "Interest and debt", line: "What borrowing really costs over ten years", hue: "#fbbf24" },
          { icon: A5, label: "Insurance", line: "What you genuinely need at your age, and what you do not", hue: "#a78bfa" },
          { icon: A6, label: "Taxes", line: "Filing, withholding, refunds and the credits people miss", hue: "#60a5fa" }
        ]}
        onStart={() => { setArrived(true); window.scrollTo({ top: 0 }); }}
      />
    </AppLayout>
  );

  return (
    <AppLayout>
      <div className="mb-4"><PatriciaPageGuide pageId="financial_literacy" /></div>
      <div className="max-w-5xl mx-auto pb-12">
        {/* Glowing hero */}
        <motion.div
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-3xl border border-amber-400/25 bg-gradient-to-br from-[#2a1c08] via-[#0a2036] to-[#08203A] p-6 sm:p-8 mb-6 drop-shadow-[0_0_36px_rgba(251,191,36,0.15)]"
        >
          <div className="pointer-events-none absolute -top-20 -right-10 w-72 h-72 rounded-full bg-amber-400/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-10 w-72 h-72 rounded-full bg-emerald-500/10 blur-3xl" />
          <div className="relative flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400/30 to-orange-600/30 ring-1 ring-amber-400/60 flex items-center justify-center text-amber-200 drop-shadow-[0_0_16px_rgba(251,191,36,0.5)]">
                <PiggyBank className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold text-white font-outfit">Financial Literacy</h1>
                <p className="text-slate-300 mt-1 text-sm sm:text-base max-w-2xl">
                  Turn the lifestyle you designed into real money skills, personalized to
                  {profile.career ? <span className="text-amber-300 font-semibold"> {profile.career}</span> : ' your goals'} and how you're wired.
                </p>
              </div>
            </div>
            <Button onClick={refresh} disabled={refreshing} variant="outline" className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 rounded-full" data-testid="finlit-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
          {(profile.target_salary || profile.monthly_cost) && (
            <div className="relative flex flex-wrap gap-2 mt-4 text-xs">
              {profile.target_salary && <span className="px-3 py-1 rounded-full bg-slate-800/60 text-slate-200">🎯 Target salary: ${Math.round(profile.target_salary).toLocaleString()}/yr</span>}
              {profile.monthly_cost && <span className="px-3 py-1 rounded-full bg-slate-800/60 text-slate-200">🏠 Monthly cost: ${Math.round(profile.monthly_cost).toLocaleString()}</span>}
              {profile.personality_type && <span className="px-3 py-1 rounded-full bg-slate-800/60 text-slate-200">🧭 {profile.personality_type}</span>}
            </div>
          )}
        </motion.div>

        {/* Lifestyle sync nudge, shown when the student updated their plan after these lessons were made */}
        {stale && !loading && (
          <motion.div
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
            className="mb-6 flex items-center justify-between gap-3 flex-wrap rounded-2xl border border-cyan-400/30 bg-cyan-500/10 p-4 drop-shadow-[0_0_22px_rgba(23,204,244,0.18)]"
            data-testid="finlit-stale-banner"
          >
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-cyan-300 flex-shrink-0" />
              <p className="text-sm text-cyan-100">
                Your Lifestyle Design changed since these lessons were made. Refresh to re-tune your money plan to your new numbers.
              </p>
            </div>
            <Button onClick={refresh} disabled={refreshing} size="sm" className="bg-cyan-500 hover:bg-cyan-400 text-[#04101f] rounded-full font-semibold" data-testid="finlit-stale-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh my plan
            </Button>
          </motion.div>
        )}

        {/* Money milestone tracker */}
        {!loading && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className={`mb-6 rounded-2xl border p-4 sm:p-5 ${milestoneMet ? 'border-amber-400/40 bg-amber-500/10 drop-shadow-[0_0_22px_rgba(251,191,36,0.2)]' : 'border-white/10 bg-slate-900/60'}`}
            data-testid="finlit-milestone-card"
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${milestoneMet ? 'bg-amber-400/25 text-amber-300' : 'bg-slate-800 text-slate-300'}`}>
                  <Trophy className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Money Smart badge</p>
                  <p className="text-slate-300 text-xs">
                    {milestoneMet
                      ? 'Earned! This booster counts toward your Launch Readiness.'
                      : `Check off ${MILESTONE_GOAL - doneCount} more tip${MILESTONE_GOAL - doneCount === 1 ? '' : 's'} you've done to earn it.`}
                  </p>
                </div>
              </div>
              <span className="text-sm font-bold text-amber-300" data-testid="finlit-milestone-count">{doneCount}/{MILESTONE_GOAL}</span>
            </div>
            <div className="mt-3 h-2 rounded-full bg-slate-800 overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-amber-400 to-emerald-400"
                initial={{ width: 0 }} animate={{ width: `${progressPct}%` }} transition={{ duration: 0.5 }}
              />
            </div>
          </motion.div>
        )}

        {loading ? (
          <div className="flex flex-col items-center py-16 gap-3">
            <LoadingSpinner />
            <p className="text-slate-300 text-sm animate-pulse">Personalizing your money plan…</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {lessons.map((l, i) => {
              const Icon = ICONS[l.icon] || Wallet;
              const accent = ACCENTS[i % ACCENTS.length];
              return (
                <motion.div
                  key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900/80 to-[#08203A]/80 backdrop-blur-xl p-5 hover:border-amber-400/30 transition-colors"
                  data-testid={`finlit-lesson-${i}`}
                >
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${accent} ring-1 flex items-center justify-center mb-3`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-white font-bold text-lg leading-tight">{l.title}</h3>
                  <p className="text-slate-300 text-sm mt-1.5">{l.why}</p>
                  <ul className="mt-3 space-y-1.5">
                    {(l.tips || []).map((t, j) => {
                      const done = completedTips.includes(t);
                      return (
                        <li key={j}>
                          <button
                            type="button"
                            onClick={() => toggleTip(t)}
                            className={`w-full flex items-start gap-2 text-left text-sm rounded-lg px-2 py-1.5 -mx-2 transition-colors ${done ? 'text-emerald-200 bg-emerald-500/10' : 'text-slate-200 hover:bg-white/5'}`}
                            data-testid={`finlit-tip-${i}-${j}`}
                          >
                            {done
                              ? <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                              : <Circle className="w-4 h-4 text-slate-300 flex-shrink-0 mt-0.5" />}
                            <span className={done ? 'line-through decoration-emerald-400/40' : ''}>{t}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        )}

        <p className="text-center text-slate-300 text-xs mt-6 flex items-center justify-center gap-1">
          <Sparkles className="w-3 h-3" /> Personalized from your Lifestyle Design & personality. Tap a tip once you've done it to build your Money Smart badge.
        </p>
      </div>
    </AppLayout>
  );
}
